// docs/V1-INTERACTIONS.md §2 — the temporary sign-in page, on the design system (plan D-4). It is drawn before any shell,
// so it carries its own McRoot: the `.mc` page paints his Background in both themes, under the 64 px bar with the
// brand lockup, the form on a Card, and the footer GoToFoods. V1's parts never render here (plan D12). The ids #u and
// #p and the accessible names the walks use (Account, Password, Sign in, Try again, Report a sign-in problem, Send)
// stay. Replaced by the GoToFoods sign-in once its registration is approved.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
// The design system by group (its one stylesheet first): this page is drawn before the shell that imports it.
import "@/mc/mc.css";
import { McRoot } from "@/mc/lib";
import { COMMON_LABELS } from "@/mc/labels";
import { Banner, Button, Card, Chip, Field, Flex, IconButton, Input, Paragraph, Stack, TextArea, type Blocked } from "@/mc/core";
import { BrandLockup, SHELL_LABELS } from "@/mc/g4a";
import { Dialog } from "@/mc/g4b";
import { useWrite } from "@/api/useTool";
import { AppError, postJson } from "@/api/runtime";
import { useSession } from "@/session/useSession";
import { adminViewAsStop } from "@/api/tools";

// The development accounts (backend/app/seed): twelve sign-ins over the eight roles (DC-228 as amended 12 Oct 05:29) —
// two Admins (an Admin never assigns or approves their own request, so a second Admin decides it), the TA Manager, a
// second Coordinator and Interviewer, and the mixed HM + Interviewer. No Root Owner wording on this page (GLOSSARY §3,
// §22).
const ACCOUNTS: { u: string; label: string }[] = [
  { u: "ta-admin", label: "Admin" },
  { u: "ta-admin-2", label: "Admin 2" },
  { u: "ta-recruiter", label: "Recruiter" },
  { u: "ta-tamanager", label: "TA Manager" },
  { u: "ta-coordinator", label: "Coordinator" },
  { u: "ta-coordinator-2", label: "Coordinator 2" },
  { u: "ta-hrbp", label: "HR Business Partner" },
  { u: "ta-comp", label: "Compensation" },
  { u: "ta-hm", label: "Hiring Manager" },
  { u: "ta-interviewer", label: "Interviewer" },
  { u: "ta-interviewer-2", label: "Interviewer 2" },
  { u: "ta-hm-interviewer", label: "HM · Interviewer" },
];

// The development seats share one password (backend/app/seed — development only; this page does not exist once the
// GoToFoods sign-in replaces it). One click fills both fields so a reviewer can switch seats without typing.
const DEV_PASSWORD = "letmein!";

// Workshop (development environment only, sessions 26 Oct to 19 Nov 2026; the client's 22 Oct 2026 request: one
// "Workshop", not Room 1 to 4): the four interviewer seats first — participants pick their assigned interviewer — then the
// facilitator seats and the Workshop Admin, who resets the Workshop from the Admin screen (workshop.reset_room). The seats
// are ordinary development accounts named workshop-<seat>; this block only saves the typing.
const WORKSHOP_INTERVIEWERS: { s: string; label: string }[] = [
  { s: "int1", label: "Interviewer 1" },
  { s: "int2", label: "Interviewer 2" },
  { s: "int3", label: "Interviewer 3" },
  { s: "int4", label: "Interviewer 4" },
];
const WORKSHOP_FACILITATORS: { s: string; label: string }[] = [
  { s: "hm", label: "Hiring Manager" },
  { s: "recruiter", label: "Recruiter" },
  { s: "hrbp", label: "HR Business Partner" },
  { s: "comp", label: "Compensation" },
  { s: "admin", label: "Workshop Admin" },
];

export const MISMATCH = "That account and password do not match.";
const REQUIRED = "Enter the account and password to sign in.";
const UNREACHABLE = "The application is not reachable right now.";
/** The server's remedy for CSRF_INVALID (backend/app/api/problems.py), should a refusal ever arrive without one. */
const RELOAD = "Reload the page and try again.";

/**
 * The sentence a refused sign-in shows (the lockout, which also counts down, is handled before this). A wrong account or
 * password reads as one uniform mismatch whether the account exists or not; a refused CSRF token is not a credential
 * failure, so it shows the server's own message for that code instead of blaming the password (F-88).
 */
export function signInError(err: unknown): string {
  if (!(err instanceof AppError)) return MISMATCH;
  if (err.unreachable) return UNREACHABLE;
  if (err.code === "CSRF_INVALID") return err.remedy || RELOAD;
  return MISMATCH; // one uniform message whether the account exists or not
}

/** The lockout's countdown sentence. */
const lockSentence = (seconds: number) => `Too many attempts. Try again in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}.`;

/**
 * The red primary (DC-23, DC-24): busy keeps its label under aria-busy; blocked — dashed and aria-disabled, never the
 * disabled attribute — while the lockout runs (described by the countdown) or while a field is empty (described by
 * the visible hint); *Try again* after a lockout or an unreachable server.
 */
export function signInPrimary(state: { busy: boolean; secondsLeft: number; filled: boolean; error: string | null }): {
  label: string; busy: boolean; blocked: Blocked | undefined;
} {
  const { busy, secondsLeft, filled, error } = state;
  const blocked: Blocked | undefined = secondsLeft > 0 ? { reason: lockSentence(secondsLeft), describedBy: "signin-error" }
    : !filled ? { reason: REQUIRED, describedBy: "signin-required" } : undefined;
  return { label: error && secondsLeft === 0 && error !== MISMATCH ? "Try again" : "Sign in", busy, blocked };
}

export function SignIn() {
  const { signIn, returnTo, signedOut } = useSession();
  const qc = useQueryClient();
  const meError = qc.getQueryState(["me"])?.error;
  const stop = useWrite(adminViewAsStop);
  const [bug, setBug] = useState(false); const [note, setNote] = useState("");
  const report = useWrite((input: { note: string }) => postJson<{ ok: boolean }>("/api/session/feedback", input));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const accountRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    accountRef.current?.focus();
  }, []);
  useEffect(() => {
    if (lockedUntil === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [lockedUntil]);

  const secondsLeft = lockedUntil === null ? 0 : Math.max(0, Math.ceil((lockedUntil - now) / 1000));
  const locked = secondsLeft > 0;
  const filled = Boolean(username && password);
  const primary = signInPrimary({ busy, secondsLeft, filled, error });

  async function submit(e: FormEvent) {
    e.preventDefault();
    // An aria-disabled primary still lets Enter submit the form: the same rules hold here.
    if (busy || primary.blocked) return;
    setBusy(true);
    setLockedUntil(null);
    setError(null);
    setRequestId(null);
    try {
      await signIn(username.trim(), password);
    } catch (err) {
      if (err instanceof AppError) {
        setRequestId(err.requestId);
        if (err.status === 429) {
          const secs = err.retryAfterSeconds ?? 15 * 60;
          setLockedUntil(Date.now() + secs * 1000);
          setNow(Date.now());
          const minutes = Math.max(1, Math.ceil(secs / 60));
          setError(`Too many attempts. Try again in ${minutes} ${minutes === 1 ? "minute" : "minutes"}.`);
        } else {
          setError(signInError(err));
        }
      } else {
        setError(signInError(err));
      }
      setPassword("");
      window.setTimeout(() => passwordRef.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  }

  function fill(account: string) {
    setUsername(account); setPassword(DEV_PASSWORD); setError(null); setRequestId(null);
    window.setTimeout(() => submitRef.current?.focus(), 0);
  }

  const required = <span className="mc-req" aria-hidden="true">{COMMON_LABELS.required}</span>;
  const sendBlocked: Blocked | undefined = note.trim() ? undefined : { reason: "Write what went wrong first." };
  const reportRefusal = report.error
    ? report.error.status === 429 ? "Too many reports from here; try again later." : report.error.remedy || "The report could not be sent. Try again."
    : undefined;

  return (
    <McRoot>
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <header className="mc-topbar"><BrandLockup /></header>
        <main style={{ flex: "1 0 auto", width: "100%", maxWidth: 520, margin: "0 auto", padding: "40px 16px 24px" }}>
          <Stack gap={16}>
            {meError instanceof AppError && meError.code === "VIEW_AS_EXPIRED" ? (
              <Banner tone="warn" right={
                <Button sm busy={stop.isPending} onClick={() => void stop.mutateAsync({}).then(() => window.location.reload()).catch(() => undefined)}>Stop viewing</Button>
              }>
                <span role="status">View-as expired. Stop viewing to return to your Admin session.</span>
              </Banner>
            ) : null}
            {stop.error ? <Banner tone="red" live>{stop.error instanceof AppError && stop.error.remedy ? stop.error.remedy : "Viewing could not be stopped. Try again."}</Banner> : null}
            <Card>
              <form onSubmit={submit} aria-labelledby="signin-title" noValidate>
                <Stack gap={16}>
                  <Stack gap={6}>
                    <h1 id="signin-title" className="mc-ph-h">Sign in to Talent Acquisition</h1>
                    <Paragraph sm>Temporary sign-in for the development environment. Production uses the GoToFoods sign-in.</Paragraph>
                  </Stack>
                  {signedOut && !error ? <div role="status"><Banner tone="info">You are signed out.</Banner></div> : null}
                  {returnTo && !signedOut ? (
                    <Paragraph sm>You will be returned to <span className="mc-mono">{returnTo}</span> after signing in.</Paragraph>
                  ) : null}
                  <Field id="u" label="Account" labelSlot={required} hint={`Development accounts: ${ACCOUNTS.map((a) => a.u).join(" · ")}.`}>
                    <Input
                      ref={accountRef}
                      autoComplete="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      invalid={error === MISMATCH}
                      aria-describedby={!locked && !filled ? "signin-required" : undefined}
                    />
                  </Field>
                  <div className="mc-chips" role="group" aria-label="Choose a development account">
                    {ACCOUNTS.map((a) => (
                      <Chip key={a.u} sm on={username === a.u} onToggle={() => fill(a.u)}>
                        {a.label} <span className="mc-mono">{a.u}</span>
                      </Chip>
                    ))}
                  </div>
                  <Stack gap={6}>
                    <Paragraph sm><strong>Workshop.</strong> Pick your assigned interviewer.</Paragraph>
                    <div className="mc-chips" role="group" aria-label="Choose your workshop interviewer">
                      {WORKSHOP_INTERVIEWERS.map((seat) => {
                        const u = `workshop-${seat.s}`;
                        return (
                          <Chip key={u} sm on={username === u} onToggle={() => fill(u)}>
                            {seat.label} <span className="mc-mono">{u}</span>
                          </Chip>
                        );
                      })}
                    </div>
                    <Paragraph sm>Facilitators</Paragraph>
                    <div className="mc-chips" role="group" aria-label="Choose a workshop facilitator seat">
                      {WORKSHOP_FACILITATORS.map((seat) => {
                        const u = `workshop-${seat.s}`;
                        return (
                          <Chip key={u} sm on={username === u} onToggle={() => fill(u)}>
                            {seat.label} <span className="mc-mono">{u}</span>
                          </Chip>
                        );
                      })}
                    </div>
                  </Stack>
                  <Field id="p" label="Password" labelSlot={required}>
                    <Flex gap={8}>
                      <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <Input
                          ref={passwordRef}
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          invalid={error === MISMATCH}
                          aria-describedby={!locked && !filled ? "signin-required" : undefined}
                        />
                      </div>
                      <IconButton icon="eye" label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword} aria-controls="p" onClick={() => setShowPassword((v) => !v)} />
                    </Flex>
                  </Field>
                  {!locked && !filled ? <Paragraph sm id="signin-required">{REQUIRED}</Paragraph> : null}
                  {error ? (
                    <Banner tone="red" live>
                      <span id="signin-error" aria-live="polite">{locked ? lockSentence(secondsLeft) : lockedUntil ? "You can try signing in again." : error}</span>
                      {requestId ? <span className="mc-mono"> · ref {requestId}</span> : null}
                    </Banner>
                  ) : null}
                  <Flex justify="between" wrap>
                    <Button ref={submitRef} type="submit" tone="primary" busy={primary.busy} blocked={primary.blocked}>{primary.label}</Button>
                    <Button tone="ghost" sm aria-haspopup="dialog" onClick={() => setBug(true)}>Report a sign-in problem</Button>
                  </Flex>
                  {report.isSuccess ? <div role="status"><Banner>Reported · we have it</Banner></div> : null}
                </Stack>
              </form>
            </Card>
          </Stack>
        </main>
        <footer style={{ padding: "16px", textAlign: "center" }}><Paragraph sm>{SHELL_LABELS.mark}</Paragraph></footer>
      </div>
      <Dialog
        title="Report a problem"
        open={bug}
        onClose={() => setBug(false)}
        dismissLabel="Cancel"
        primary={{ label: "Send", blocked: sendBlocked, busy: report.isPending, onClick: () => report.mutate({ note }, { onSuccess: () => { setBug(false); setNote(""); } }) }}
        refused={reportRefusal ? { reason: reportRefusal } : undefined}
      >
        <Stack>
          <Paragraph>Only your note is sent. No account or screen identifiers are included.</Paragraph>
          <Field label="What went wrong, and what did you expect?" id="anonymous-note" labelSlot={required}>
            <TextArea required maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </Stack>
      </Dialog>
    </McRoot>
  );
}
